import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Header } from '@/components/layout/Header';
import { FCRAFooterText } from '@/components/layout/FCRADisclaimer';
import {
  Search,
  FileText,
  Zap,
  Shield,
  BarChart3,
  Users,
  ArrowRight,
  CheckCircle,
  Brain,
  Database,
  Globe
} from 'lucide-react';

// Declare type for HubSpot
declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    hbspt: any;
  }
}

export const CorporateLandingPage: React.FC = () => {
  const contactFormRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Set document title and meta tags for SEO
    document.title = 'CollectABILITY - AI-Powered Debt Collection Intelligence | Smarter Collections, Higher Returns';
    
    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, property?: boolean) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Basic SEO meta tags
    updateMetaTag('description', 'Transform your debt collection strategy with AI-powered intelligence. Batch processing, web search, and API access for collectability scoring. Try for free today.');
    updateMetaTag('keywords', 'debt collection, AI, collectability scoring, batch processing, debt recovery, collection intelligence, skip tracing');
    updateMetaTag('robots', 'index, follow');
    
    // Open Graph meta tags
    updateMetaTag('og:type', 'website', true);
    updateMetaTag('og:title', 'CollectABILITY - AI-Powered Debt Collection Intelligence', true);
    updateMetaTag('og:description', 'Smarter Collections. Higher Returns. Powered by AI. Transform your debt collection strategy with our intelligence platform.', true);
    updateMetaTag('og:image', `${window.location.origin}/stack-logo-1000.png`, true);
    updateMetaTag('og:url', window.location.href, true);
    updateMetaTag('og:site_name', 'CollectABILITY', true);
    
    // Twitter Card meta tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', 'CollectABILITY - AI-Powered Debt Collection Intelligence');
    updateMetaTag('twitter:description', 'Smarter Collections. Higher Returns. Powered by AI.');
    updateMetaTag('twitter:image', `${window.location.origin}/stack-logo-1000.png`);
    
    // Add structured data
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "CollectABILITY",
      "description": "AI-powered debt collection intelligence platform for smarter collections and higher returns. Batch processing, web search, and API access for collectability scoring.",
      "url": window.location.origin,
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web",
      "offers": {
        "@type": "Offer",
        "name": "CollectABILITY Services",
        "description": "Try searching for FREE, no reporting for FREE"
      },
      "creator": {
        "@type": "Organization",
        "name": "CollectABILITY"
      }
    };

    let structuredDataScript = document.querySelector('script[type="application/ld+json"]');
    if (!structuredDataScript) {
      structuredDataScript = document.createElement('script');
      structuredDataScript.setAttribute('type', 'application/ld+json');
      document.head.appendChild(structuredDataScript);
    }
    structuredDataScript.textContent = JSON.stringify(structuredData);

    // Add preload links for performance
    const addPreloadLink = (href: string, as: string) => {
      if (!document.querySelector(`link[rel="preload"][href="${href}"]`)) {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.href = href;
        link.as = as;
        document.head.appendChild(link);
      }
    };

    addPreloadLink('/linear-logo-sm.png', 'image');

    // Initialize HubSpot form
    const initHubSpotForm = () => {
      if (window.hbspt) {
        window.hbspt.forms.create({
          portalId: "39894199",
          formId: "5168a2f5-e36d-484e-b13d-bba41c2a35f9",
          region: "na1",
          target: "#hubspot-form-container"
        });
      } else {
        // Retry after a short delay if HubSpot script isn't loaded yet
        setTimeout(initHubSpotForm, 100);
      }
    };

    // Initialize form after a short delay to ensure DOM is ready
    setTimeout(initHubSpotForm, 500);
  }, []);

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="pt-16 pb-20 lg:pt-20 lg:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-6">
                Smarter Collections.<br />
                Higher Returns.<br />
                <span className="text-blue-600">Powered by AI.</span>
              </h1>
              
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Take years of debt collection strategy from our Intelligence data to optimize returns on everything from leads to assets, with a proprietary algorithm that gets you to answers fast.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/batch"
                  className="bg-blue-600 hover:bg-blue-800 text-white px-8 py-3 rounded-lg font-semibold transition-colors text-center flex items-center justify-center gap-2"
                >
                  Try Batch Processing
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/search"
                  className="bg-slate-900 hover:bg-slate-800 text-white px-8 py-3 rounded-lg font-semibold transition-colors text-center flex items-center justify-center gap-2"
                >
                  Try Web Search
                  <Search className="w-4 h-4" />
                </Link>
              </div>
              
              <p className="text-sm text-slate-500 mt-4">
                No Subscription or Contract Necessary • One Time Purchase Allowed
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <img
                src="/laptop-search.png"
                alt="CollectABILITY Platform"
                className="w-full h-auto"
                loading="eager"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Powerful Tools for Every Workflow
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Choose the right solution for your needs—from bulk processing to individual searches to seamless API integration.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-lg p-4  border border-slate-200"
            >
              <div className="w-12 h-10 bg-slate-100 rounded-lg flex items-center justify-center mb-4">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3">Batch Processing</h3>
              <p className="text-slate-600 mb-4">
                Upload CSV files and process thousands of records at once with AI-powered validation, enrichment, and scoring.
              </p>
              <Link
                to="/batch"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="text-blue-600 hover:text-slate-900 font-medium text-sm flex items-center gap-1"
              >
                Try Batch Processing <ArrowRight className="w-3 h-3" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white rounded-lg p-4  border border-slate-200"
            >
              <div className="w-12 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <Search className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3">Web Search Portal</h3>
              <p className="text-slate-600 mb-4">
                Search individual contacts across multiple data sources for instant validation and enrichment results.
              </p>
              <Link
                to="/search"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="text-blue-600 hover:text-slate-900 font-medium text-sm flex items-center gap-1"
              >
                Try Web Search <ArrowRight className="w-3 h-3" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white rounded-lg p-4  border border-slate-200"
            >
              <div className="w-12 h-10 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3">API Access</h3>
              <p className="text-slate-600 mb-4">
                Integrate our intelligence directly into your applications with RESTful APIs and real-time processing.
              </p>
              <a
                href="#contact"
                className="text-blue-600 hover:text-slate-900 font-medium text-sm flex items-center gap-1"
              >
                Coming Soon <ArrowRight className="w-3 h-3" />
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* AI-Powered Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              AI-Powered Recovery Scoring for<br />
              Informed Decision Making
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Using our part of the recovery stack CollectABILITY is the go-to solution for smarter debt collections.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-center">
            <div className="space-y-3">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Brain className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">Advanced Contact Discovery</h3>
                  <p className="text-slate-600 text-sm">
                    Instantly verify 55 data sources and your latest debt collection through AI's proprietary algorithm for smart debt collection.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Database className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">Data Enrichment</h3>
                  <p className="text-slate-600 text-sm">
                    Enhanced data via pre-optimized lead opportunities and connect with consumers using complete background intelligence and data mining.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibuild text-slate-900 mb-2">Predictive Intelligence</h3>
                  <p className="text-slate-600 text-sm">
                    Segmented data by intelligence and portfolio/class, asset risk, and cost to provide a complete solution.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-center">
              <div className="relative">
                <div className="w-64 h-64 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center">
                  <div className="w-40 h-40 bg-white rounded-full  flex items-center justify-center">
                    <Brain className="w-16 h-16 text-blue-600" />
                  </div>
                </div>
                <div className="absolute -top-4 -right-4 w-12 h-10 bg-green-500 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <div className="absolute -bottom-4 -left-4 w-12 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                  <Globe className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">Consumer Profile Discovery</h3>
                  <p className="text-slate-600 text-sm">
                    Comprehensive consumer investigations covering relatives, assets, phone numbers, and debt/employer financial information.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Users className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">Quality Recommendations</h3>
                  <p className="text-slate-600 text-sm">
                    CollectABILITY's Solution recommendations and skip tracing are powered by its proprietary algorithm for maximum effectiveness.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="w-5 h-5 text-pink-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">Proprietary Intelligence</h3>
                  <p className="text-slate-600 text-sm">
                    More data from our collection databases, intelligence enrichment, and marketing across multiple industries.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Ready to transform your collections strategy?
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Stop chasing dead ends, improve customer experience, and transform your collections strategies with CollectABILITY. Schedule a demo to find out how.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link
              to="/batch"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors"
            >
              Try Batch Processing
            </Link>
            <Link
              to="/search"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="bg-white hover:bg-slate-50 text-blue-600 px-8 py-3 rounded-lg font-semibold transition-colors"
            >
              Try Web Search
            </Link>
          </div>

          {/* HubSpot Contact Form */}
          <div id="contact" ref={contactFormRef} className="mt-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-lg  p-8 lg:p-12"
            >
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-4">
                  Get in Touch
                </h3>
                <p className="text-slate-600">
                  Want to learn more? Fill out the form below and we'll get back to you.
                </p>
              </div>

              <div id="hubspot-form-container"></div>

              <div className="text-center mt-8">
                <div className="flex items-center justify-center gap-3 text-sm text-slate-500">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Free consultation</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>No commitment required</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-200 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            {/* Logo & About */}
            <div className="col-span-1 md:col-span-2">
              <Link to="/" className="flex items-center mb-4" aria-label="CollectABILITY Home">
                <img
                  src="/linear-logo-sm.png"
                  alt="CollectABILITY"
                  className="h-10"
                  loading="lazy"
                  height="40"
                />
              </Link>
              <p className="text-slate-700 text-sm mb-4 max-w-md">
                AI-powered debt collection intelligence platform for smarter collections and higher returns.
              </p>
              <div className="flex items-center gap-4">
                <a
                  href="mailto:support@collectability.ai"
                  className="text-slate-700 hover:text-slate-900 text-sm transition-colors"
                >
                  support@collectability.ai
                </a>
              </div>
            </div>

            {/* Product Links */}
            <div>
              <h3 className="text-slate-900 font-semibold mb-4">Product</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/batch" className="text-slate-700 hover:text-slate-900 text-sm transition-colors">
                    Batch Processing
                  </Link>
                </li>
                <li>
                  <Link to="/search" className="text-slate-700 hover:text-slate-900 text-sm transition-colors">
                    Web Search
                  </Link>
                </li>
                <li>
                  <a href="/#features" className="text-slate-700 hover:text-slate-900 text-sm transition-colors">
                    Features
                  </a>
                </li>
              </ul>
            </div>

            {/* Company Links */}
            <div>
              <h3 className="text-slate-900 font-semibold mb-4">Company</h3>
              <ul className="space-y-2">
                <li>
                  <a href="/#contact" className="text-slate-700 hover:text-slate-900 text-sm transition-colors">
                    Contact Us
                  </a>
                </li>
                <li>
                  <Link to="/terms" className="text-slate-700 hover:text-slate-900 text-sm transition-colors">
                    Terms of Use
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-slate-700 hover:text-slate-900 text-sm transition-colors">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/account" className="text-slate-700 hover:text-slate-900 text-sm transition-colors">
                    My Account
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-300 pt-8 space-y-3">
            <FCRAFooterText />
            <p className="text-slate-700 text-sm text-center">
              &copy; 2024 CollectABILITY, LLC. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

